import { useEffect, useRef, useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

const IDLE_TIMEOUT = 4 * 60 * 60 * 1000; // 4 hours
const WARNING_BEFORE = 5 * 60 * 1000; // 5 minutes before logout
const CHECK_INTERVAL = 30 * 1000; // Check every 30 seconds
const EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
const LAST_ACTIVITY_KEY_PREFIX = 'productdocs_last_activity_at';
const SESSION_EXPIRED_KEY_PREFIX = 'productdocs_session_expired_at';
const GLOBAL_STORAGE_SUFFIX = 'global';

const buildStorageKey = (prefix: string, userId: string | null) => `${prefix}:${userId ?? GLOBAL_STORAGE_SUFFIX}`;

export function useIdleTimeout() {
  const [showWarning, setShowWarning] = useState(false);
  const lastActivityRef = useRef<number>(Date.now());
  const activeRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentUserIdRef = useRef<string | null>(null);

  const getActivityKey = useCallback((userId?: string | null) => {
    return buildStorageKey(LAST_ACTIVITY_KEY_PREFIX, userId ?? currentUserIdRef.current);
  }, []);

  const getSessionExpiredKey = useCallback((userId?: string | null) => {
    return buildStorageKey(SESSION_EXPIRED_KEY_PREFIX, userId ?? currentUserIdRef.current);
  }, []);

  const updateActivity = useCallback((timestamp: number, userId?: string | null) => {
    const resolvedUserId = userId ?? currentUserIdRef.current;

    lastActivityRef.current = timestamp;
    localStorage.setItem(getActivityKey(resolvedUserId), String(timestamp));
    localStorage.setItem(buildStorageKey(LAST_ACTIVITY_KEY_PREFIX, null), String(timestamp));
  }, [getActivityKey]);

  const handleLogout = useCallback(async (expired = false, userId?: string | null) => {
    setShowWarning(false);
    activeRef.current = false;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (expired) {
      const value = String(Date.now());
      const resolvedUserId = userId ?? currentUserIdRef.current;

      localStorage.setItem(getSessionExpiredKey(resolvedUserId), value);
      localStorage.setItem(buildStorageKey(SESSION_EXPIRED_KEY_PREFIX, null), value);
    }

    try {
      await supabase.auth.signOut({ scope: 'global' });
    } catch (e) {
      console.error('Erro ao fazer logout por inatividade:', e);
    }

    window.location.href = expired ? '/?sessionExpired=1' : '/';
  }, [getSessionExpiredKey]);

  const recordActivity = useCallback(() => {
    if (!activeRef.current) return;

    updateActivity(Date.now());
    setShowWarning(false);
  }, [updateActivity]);

  const continueSession = useCallback(() => {
    updateActivity(Date.now());
    setShowWarning(false);
  }, [updateActivity]);

  useEffect(() => {
    const checkIdle = () => {
      if (!activeRef.current) return;

      const elapsed = Date.now() - lastActivityRef.current;

      if (elapsed >= IDLE_TIMEOUT) {
        handleLogout(true);
      } else if (elapsed >= IDLE_TIMEOUT - WARNING_BEFORE) {
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }
    };

    const startListening = (userId: string, isFreshLogin = false) => {
      if (activeRef.current) return;

      currentUserIdRef.current = userId;
      const now = Date.now();

      // On fresh login, always reset activity — never expire based on stale timestamps
      if (!isFreshLogin) {
        const userStoredActivity = Number(localStorage.getItem(getActivityKey(userId)));
        const globalStoredActivity = Number(localStorage.getItem(buildStorageKey(LAST_ACTIVITY_KEY_PREFIX, null)));
        const validStoredActivity = [userStoredActivity, globalStoredActivity].find(
          (value) => Number.isFinite(value) && value > 0
        );
        const lastKnownActivity = validStoredActivity ?? now;

        if (now - lastKnownActivity >= IDLE_TIMEOUT) {
          handleLogout(true, userId);
          return;
        }
      }

      // Clear any previous expired flags for this user
      localStorage.removeItem(getSessionExpiredKey(userId));
      localStorage.removeItem(buildStorageKey(SESSION_EXPIRED_KEY_PREFIX, null));

      activeRef.current = true;
      updateActivity(now, userId);

      EVENTS.forEach(event => window.addEventListener(event, recordActivity, { passive: true }));
      intervalRef.current = setInterval(checkIdle, CHECK_INTERVAL);
    };

    const stopListening = () => {
      activeRef.current = false;
      setShowWarning(false);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      EVENTS.forEach(event => window.removeEventListener(event, recordActivity));
    };

    const onStorage = (event: StorageEvent) => {
      if (!event.key || !event.newValue) return;

      const currentUserId = currentUserIdRef.current;
      const relevantActivityKeys = [
        getActivityKey(currentUserId),
        buildStorageKey(LAST_ACTIVITY_KEY_PREFIX, null),
      ];

      if (relevantActivityKeys.includes(event.key)) {
        const timestamp = Number(event.newValue);

        if (Number.isFinite(timestamp) && timestamp > 0) {
          lastActivityRef.current = timestamp;
          setShowWarning(false);
        }
      }

      const relevantExpiredKeys = [
        getSessionExpiredKey(currentUserId),
        buildStorageKey(SESSION_EXPIRED_KEY_PREFIX, null),
      ];

      if (relevantExpiredKeys.includes(event.key)) {
        handleLogout(true, currentUserId);
      }
    };

    window.addEventListener('storage', onStorage);

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) startListening(session.user.id);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        stopListening();
        currentUserIdRef.current = null;
      } else if (event === 'SIGNED_IN' && session) {
        currentUserIdRef.current = session.user.id;
        if (!activeRef.current) {
          startListening(session.user.id, true);
        } else {
          updateActivity(Date.now(), session.user.id);
        }
      } else if (session && !activeRef.current) {
        startListening(session.user.id, false);
      }
    });

    return () => {
      window.removeEventListener('storage', onStorage);
      stopListening();
      subscription.unsubscribe();
    };
  }, [handleLogout, recordActivity, updateActivity, getActivityKey, getSessionExpiredKey]);

  return { showWarning, continueSession, logout: () => handleLogout(false) };
}
